import {Component, Inject} from '@angular/core';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { defaultCapacityHours } from '../../common/organisation';
import { OrganisationService } from '../../organisation/organisation.service';
import { FREQUENCY_TYPE, DAYS_OF_WEEK, getDurationDescription, Duration, NODE_SUPERTYPE, createFrequency, Frequency, NODE_TYPE, NodeProperties, durationEquals, frequencyEquals } from '../../common/linqset-model';

export interface FrequencyDialogData {
    properties:NodeProperties
    superType:NODE_SUPERTYPE
    type?:NODE_TYPE
    calculatedNum?:number   // the incoming frequency for actions, used when the frequency is calculated
    readonly?:boolean
}

export interface FrequencyDialogResult {
    durationChange:boolean
    frequencyChange:boolean
    duration:Duration
    frequency:Frequency
}

const DURATION_FIELDS:{field:keyof Duration, label:string}[] = [
    {field:'days', label:'Days'}, {field:'hours', label:'Hours'}, {field:'minutes', label:'Minutes'}, {field:'seconds', label:'Seconds'}
]

// the frequency choices, in the order they are offered
const FREQUENCY_CHOICES = [FREQUENCY_TYPE.DAY, FREQUENCY_TYPE.WORK_DAYS, FREQUENCY_TYPE.SPECIFIC_DAYS, FREQUENCY_TYPE.WEEK,
    FREQUENCY_TYPE.FORTNIGHT, FREQUENCY_TYPE.MONTH, FREQUENCY_TYPE.QUARTER, FREQUENCY_TYPE.YEAR]

const WEEKLY_TYPES = [FREQUENCY_TYPE.WORK_DAYS, FREQUENCY_TYPE.SPECIFIC_DAYS, FREQUENCY_TYPE.WEEK, FREQUENCY_TYPE.FORTNIGHT]

// edits how long an action takes and how often it happens, or the time worked by a role or person.
// works on copies and closes with them, so cancelling leaves the node untouched
@Component({
    selector: 'app-frequency-dialog',
    templateUrl: 'frequency-dialog.html',
    styleUrls: ['frequency-dialog.css'],
    standalone: false
})
export class FrequencyDialogComponent {
    FREQUENCY_TYPE = FREQUENCY_TYPE
    DAYS_OF_WEEK = DAYS_OF_WEEK
    DURATION_FIELDS = DURATION_FIELDS
    FREQUENCY_CHOICES = FREQUENCY_CHOICES
    calculatedNum:number
    isAction:boolean
    readonly:boolean

    excludeUseCalculated:boolean
    frequency:Frequency
    duration:Duration
    useInputFrequency:boolean

    private originalFrequency:Frequency|undefined
    private originalDuration:Duration|undefined

    constructor(public dialogRef: MatDialogRef<FrequencyDialogComponent, FrequencyDialogResult>, @Inject(MAT_DIALOG_DATA) public data: FrequencyDialogData,
        private organisationService:OrganisationService) {
        const properties = data.properties || {} as NodeProperties
        this.isAction = data.superType === NODE_SUPERTYPE.ACTION
        this.readonly = !!data.readonly
        this.calculatedNum = data.calculatedNum

        this.originalDuration = properties.duration
        this.originalFrequency = properties.frequency
        this.duration = properties.duration ? {...properties.duration} : {} as Duration

        const frequency = properties.frequency || createFrequency(data.superType, data.type) || {type:FREQUENCY_TYPE.WORK_DAYS}
        this.frequency = {...frequency, days:frequency.days ? [...frequency.days] : undefined}
        if (!this.frequency.days) delete this.frequency.days

        // people and capture nodes always need a frequency of their own
        this.excludeUseCalculated = data.superType === NODE_SUPERTYPE.PEOPLE || data.type === NODE_TYPE.CAPTURE
        if (this.excludeUseCalculated && this.frequency.type === FREQUENCY_TYPE.USE_CALCULATED) this.frequency.type = FREQUENCY_TYPE.WORK_DAYS

        if (this.frequency.ocurrences === undefined && !this.isAction) this.frequency.ocurrences = 1
        this.useInputFrequency = this.frequency.type === FREQUENCY_TYPE.USE_CALCULATED
    }

    getTitle() {
        return this.isAction ? 'Duration and frequency' : 'Time worked'
    }

    getHeadingText() {
        switch (this.data.superType) {
            case NODE_SUPERTYPE.ACTION:
                return "How long does this action take per item?"
            case NODE_SUPERTYPE.ROLE:
                return "Enter typical time worked for each person in this role. This will be multiplied by the number of people in the role."
            case NODE_SUPERTYPE.PEOPLE:
                if (this.data.type === NODE_TYPE.TEAM)
                    return "Enter typical time worked for each person in this team. This will be multiplied by the number of people in the team."
                else return "Enter time worked for this person. This is used to calculate capacity for roles and systems."
        }
        return ""
    }

    // the organisation's full-time hours a year, which a role without its own capacity has for each of its people
    get defaultHours() {
        return defaultCapacityHours(this.organisationService.organisation)
    }

    getToggleHint() {
        if (this.isAction) return 'Use the frequency of the items coming into this action, or set how often it happens.'
        return this.defaultHours
            ? `Set the time worked, or use the default capacity from the organisation settings: ${this.defaultHours.toLocaleString()} hours a year for each of the role's people.`
            : 'Set the time worked, or use the default capacity from the organisation settings. None is set there, so capacity matches the hours needed and gives no insight into workload.'
    }

    getCalculatedLabel() {
        if (this.useInputFrequency) {
            if (this.calculatedNum) {
                return this.isAction?`Uses input frequency of ${this.calculatedNum} per annum`:`Uses people capacity of ${this.calculatedNum} hours per annum`
            }else {
                return this.isAction ? `Uses input frequency`
                    : this.defaultHours ? `Use the default capacity (${this.defaultHours.toLocaleString()} hours a person)` : `Use the default capacity`
            }
        }else {
            return this.isAction?`Override input frequency`:'Set capacity'
        }
    }

    getDurationDescription() {
        return getDurationDescription(this.duration)
    }

    showDuration() {
        return this.isAction || !this.useInputFrequency
    }

    showFrequency() {
        return !this.useInputFrequency
    }

    usesWeeksPerYear() {
        return WEEKLY_TYPES.includes(this.frequency.type)
    }

    isDayChecked(day:string) {
        return !!this.frequency.days?.includes(day)
    }

    // keeps the days in week order, so the same choice always compares equal
    checkBoxChange(evt:MatCheckboxChange, day:string) {
        const days = new Set(this.frequency.days || [])
        if (evt.checked) days.add(day)
        else days.delete(day)
        this.frequency.days = DAYS_OF_WEEK.filter(d => days.has(d))
    }

    changeInputFrequency(evt:MatSlideToggleChange) {
        this.useInputFrequency = evt.checked
        if (evt.checked) {
            this.frequency.type = FREQUENCY_TYPE.USE_CALCULATED
        }else {
            this.frequency.type = FREQUENCY_TYPE.WORK_DAYS
            // an action set to happen no times at all would do no work
            if (this.frequency.ocurrences === undefined) this.frequency.ocurrences = 1
        }
    }

    private isBlank(value:any) {
        return value === undefined || value === null || value === ''
    }

    private isNegative(value:any) {
        return !this.isBlank(value) && (isNaN(Number(value)) || Number(value) < 0)
    }

    durationError():string|undefined {
        if (!this.showDuration()) return undefined
        if (DURATION_FIELDS.some(f => this.isNegative(this.duration[f.field]))) return 'Times can\'t be negative'
        return undefined
    }

    occurrencesError():string|undefined {
        if (!this.isAction || !this.showFrequency()) return undefined
        if (this.isBlank(this.frequency.ocurrences)) return 'Enter how many times'
        if (this.isNegative(this.frequency.ocurrences)) return 'Can\'t be negative'
        return undefined
    }

    daysError():string|undefined {
        if (!this.showFrequency() || this.frequency.type !== FREQUENCY_TYPE.SPECIFIC_DAYS) return undefined
        return this.frequency.days?.length ? undefined : 'Choose at least one day'
    }

    perYearError():string|undefined {
        if (!this.showFrequency()) return undefined
        if (this.frequency.type === FREQUENCY_TYPE.DAY) return this.outOfRange(this.frequency.daysPerYear, 366, 'days')
        if (this.usesWeeksPerYear()) return this.outOfRange(this.frequency.weeksPerYear, 53, 'weeks')
        return undefined
    }

    private outOfRange(value:any, max:number, unit:string) {
        if (this.isBlank(value)) return undefined
        const n = Number(value)
        return (isNaN(n) || n < 1 || n > max) ? `Must be between 1 and ${max} ${unit}` : undefined
    }

    isValid() {
        return !this.durationError() && !this.occurrencesError() && !this.daysError() && !this.perYearError()
    }

    // blank fields are left out rather than saved as empty strings
    private cleanDuration():Duration {
        const duration = {} as Duration
        DURATION_FIELDS.forEach(f => {
            const value = this.duration[f.field]
            if (!this.isBlank(value)) duration[f.field] = Number(value)
        })
        return duration
    }

    // keeps any other fields the frequency has; only the numbers edited here are tidied
    private cleanFrequency():Frequency {
        const frequency:Frequency = {...this.frequency}
        const fields:(keyof Frequency)[] = ['ocurrences', 'daysPerYear', 'weeksPerYear']
        fields.forEach(field => {
            if (this.isBlank(frequency[field])) delete frequency[field]
            else (frequency as any)[field] = Number(frequency[field])
        })
        if (frequency.days) frequency.days = [...frequency.days]
        return frequency
    }

    save() {
        if (this.readonly || !this.isValid()) return

        const duration = this.cleanDuration()
        const frequency = this.cleanFrequency()
        this.dialogRef.close({
            duration, frequency,
            durationChange: !durationEquals(this.originalDuration, duration) && !(this.originalDuration === undefined && durationEquals({} as Duration, duration)),
            frequencyChange: !this.originalFrequency || !frequencyEquals(this.originalFrequency, frequency),
        })
    }
}
